import React from 'react';
import PropTypes from 'prop-types';

// VDS-React
import ReactAriaModal from 'react-aria-modal';
import Progress from '@visa/vds-react/progress-styled';
import CybsDialog from '@cybs-ui/components/lib/dialog';
import CybsButton from '@cybs-ui/components/lib/button';
import CybsScreenReaderManager from '@cybs-ui/components/lib/screen-reader-manager';

import styles from './component.styles';

function CybsLoader({
  className,
  closeLabel,
  colorScheme,
  dataTest,
  isDeterminate,
  isDialog,
  isPageLoad,
  isVisible,
  onCloseDialog,
  percentage,
  role,
  size,
  titleId,
  title,
  i18n: i18nProp,
  type,
  modalStyle,
  ...props
}) {
  // Use localization defaults for undefined strings
  const i18n = {
    ...CybsLoader.defaultProps.i18n,
    ...i18nProp
  };

  if (isVisible) {
    CybsScreenReaderManager.addNotification(i18n.ariaLabel);
    if (type === 'linear') {
      return (
        <Progress
          determinate={isDeterminate}
          className={className}
          data-test={`${dataTest}-cybsLoader`}
          type={type}
          size={size}
          value={percentage}
          aria-label={i18n.ariaLabel}
          role={role}
        />
      );
    }

    if (isDialog) {
      return (
        <CybsDialog
          dataTest={dataTest}
          isOpen={isDialog}
          onExit={onCloseDialog}
          title={title || i18n.title}
          css={theme => styles(theme).dialog}
          modalStyle={modalStyle}
          body={
            <Progress
              determinate={isDeterminate}
              className={className}
              data-test={`${dataTest}-cybsLoader`}
              type={type}
              size={size}
              percentage={percentage}
              aria-label={i18n.ariaLabel}
              role={role}
            />
          }
          customFooter={
            <CybsButton
              dataTest={dataTest}
              colorScheme={colorScheme}
              i18n={{ label: closeLabel }}
              onClick={onCloseDialog}
            />
          }
          {...props}
        />
      );
    }

    if (isPageLoad) {
      return (
        <ReactAriaModal
          titleId="modal-loader"
          onExit={onCloseDialog}
          underlayStyle={modalStyle}
        >
          {/* id modal-loader is used by Panel/Dialog focus logic */}
          <p id="modal-loader" className="cybs-a11y-hidden">
            {i18n.indeterminateSpinnerLabel}
          </p>
          <div css={theme => styles(theme).default}>
            <Progress
              determinate={isDeterminate}
              className={className}
              data-test={`${dataTest}-cybsLoader`}
              size={size}
              type={type}
              value={percentage}
              tabIndex={1} // eslint-disable-line
              aria-label={i18n.ariaLabel}
              role={role}
            />
          </div>
        </ReactAriaModal>
      );
    }

    return (
      <Progress
        determinate={isDeterminate}
        className={className}
        data-test={`${dataTest}-cybsLoader`}
        size={size}
        type={type}
        value={percentage}
        customCSS={theme => styles(theme).progress}
        aria-label={i18n.ariaLabel}
        role={role}
      />
    );
  }

  return null;
}

CybsLoader.propTypes = {
  /**
   * Strings for component
   */
  i18n: PropTypes.shape({
    title: PropTypes.string,
    ariaLabel: PropTypes.string,
    indeterminateSpinnerLabel: PropTypes.string
  }),
  /**
   * confirm button colorScheme
   */
  colorScheme: PropTypes.string,

  /**
   * Label for the close button in the modal.
   */
  closeLabel: PropTypes.string,

  /**
   * If true, the loader has a determinate value
   */
  isDeterminate: PropTypes.bool,

  /**
   * Callback fired when hitting escape or clicking on the close button.
   */
  onCloseDialog: PropTypes.func,

  /**
   * If true, the loader is in a dialog.
   */
  isDialog: PropTypes.bool,

  /**
   * If true, it will block the whole page
   */
  isPageLoad: PropTypes.bool,

  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   * Data test
   */
  dataTest: PropTypes.string.isRequired,

  /**
   * If true, the loader is visible.
   */
  isVisible: PropTypes.bool,
  /**
   * role
   */
  role: PropTypes.string,
  /**
   * Size of the loader or progress bar.
   */
  size: PropTypes.string,

  /**
   * Title for the dialog  Deprecated. Included for backward compatability. Use i18n prop for strings.
   */
  title: PropTypes.string,

  /**
   * React Aria Modal used by VDS-Dialog required titleText or titleId if not title should be displayed.
   */
  titleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * @ignore
   */
  type: PropTypes.oneOf(['linear', 'spinner']),

  /**
   *  Completed percentage displayed by the loader when isDeterminate is true.
   */
  percentage: PropTypes.number,

  /**
   * Custom object styles for the underlayStyle prop from react-aria-modal
   *
   * @ignore
   */
  modalStyle: PropTypes.shape({})
};

CybsLoader.defaultProps = {
  i18n: {
    title: 'Loading',
    ariaLabel: 'Loading',
    indeterminateSpinnerLabel: 'loader'
  },
  className: '',
  closeLabel: 'close',
  colorScheme: 'secondary',
  isDeterminate: false,
  isDialog: false,
  isPageLoad: false,
  isVisible: false,
  onCloseDialog: () => {},
  percentage: 0,
  role: 'img',
  size: 'large',
  title: '',
  titleId: '',
  type: 'spinner',
  modalStyle: { zIndex: 999999 }
};

CybsLoader.displayName = 'CybsLoader';
export default CybsLoader;
